import {useCallback, useEffect, useRef, useState} from 'react';

const closed = {phase: 'closed'};
const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function useGalleryOverlaySession(containerRef, openViewer) {
    const [session, setSession] = useState(closed);
    const current = useRef(closed);
    const viewer = useRef(null);
    const origin = useRef(null);
    const historyOwned = useRef(false);
    const overviewRef = useRef(null);
    const overviewScroll = useRef(0);
    const mounted = useRef(true);
    const move = useCallback(next => {
        current.current = next;
        if (mounted.current) setSession(next);
    }, []);
    const active = session.phase !== 'closed';

    const finish = useCallback(() => {
        move(closed);
        const previous = origin.current;
        origin.current = null;
        if (previous) requestAnimationFrame(() => {
            if (!mounted.current || current.current.phase !== 'closed') return;
            window.scrollTo({top: previous.scrollY, behavior: 'auto'});
            const target = previous.element?.isConnected ? previous.element : containerRef.current;
            target?.focus?.({preventScroll: true});
            window.history.scrollRestoration = previous.scrollRestoration;
        });
    }, [move, containerRef]);

    const releaseHistory = useCallback(() => {
        if (!historyOwned.current) return;
        historyOwned.current = false;
        window.history.back();
    }, []);

    const close = useCallback((fromHistory = false) => {
        if (current.current.phase === 'closed' || current.current.phase === 'closing') return;
        if (!fromHistory) releaseHistory();
        if (viewer.current) {
            move({phase: 'closing'});
            viewer.current.close();
        } else {
            const wasOverview = current.current.phase === 'overview' || current.current.phase === 'opening-viewer';
            move({phase: 'closing'});
            if (!wasOverview) finish();
        }
    }, [finish, move, releaseHistory]);

    const start = event => {
        if (current.current.phase !== 'closed') return;
        const computed = getComputedStyle(containerRef.current);
        const style = {fontFamily: computed.fontFamily, color: computed.color};
        ['--container-roundness', '--button-roundness', '--primary-button-background-colour', '--text-colour'].forEach(name => {
            style[name] = computed.getPropertyValue(name);
        });
        origin.current = {element: event.currentTarget, scrollY: window.scrollY, style,
            scrollRestoration: window.history.scrollRestoration};
        window.history.scrollRestoration = 'manual';
        window.history.pushState(window.history.state, '', window.location.href);
        historyOwned.current = true;
    };

    const showOverview = event => {
        start(event);
        move({phase: 'overview', index: 0});
    };
    const showImage = (index, event, fromOverview = false) => {
        if (fromOverview) {
            overviewScroll.current = overviewRef.current?.scrollTop || 0;
            move({phase: 'opening-viewer', index});
        } else {
            start(event);
            move({phase: 'viewer', index, returnTo: 'page'});
            openViewer(index);
        }
    };
    const afterOverviewLeave = () => {
        if (current.current.phase === 'opening-viewer') {
            const index = current.current.index;
            move({phase: 'viewer', index, returnTo: 'overview'});
            openViewer(index);
        } else if (current.current.phase === 'closing' && !viewer.current) finish();
    };

    const onViewerOpen = instance => {
        viewer.current = instance;
        const template = instance.template;
        template.setAttribute('aria-label', 'Gallery Viewer');
        const focusViewer = () => {
            if (viewer.current === instance) {
                const note = instance.currItem?.container?.querySelector('.gallery-viewer-note');
                (note || template.querySelector('.pswp__button--close'))?.focus({preventScroll: true});
            }
        };
        const trap = event => {
            if (event.key !== 'Tab') return;
            const controls = Array.from(template.querySelectorAll(focusableSelector)).filter(element =>
                element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
                && (!element.closest('.pswp__item') || instance.currItem?.container?.contains(element)));
            if (!controls.length) return;
            const index = controls.indexOf(document.activeElement);
            event.preventDefault();
            const next = event.shiftKey ? (index <= 0 ? controls.length - 1 : index - 1) : (index + 1) % controls.length;
            controls[next].focus({preventScroll: true});
        };
        template.addEventListener('keydown', trap, true);
        instance.listen('initialZoomInEnd', focusViewer);
        requestAnimationFrame(focusViewer);
        instance.listen('afterChange', () => {
            if (current.current.phase === 'viewer') {
                move({...current.current, index: instance.getCurrentIndex()});
                focusViewer();
            }
        });
        instance.listen('destroy', () => {
            template.removeEventListener('keydown', trap, true);
            viewer.current = null;
            if (!mounted.current) return;
            if (current.current.phase === 'viewer' && current.current.returnTo === 'overview') {
                move({phase: 'overview', index: current.current.index});
            } else {
                releaseHistory();
                finish();
            }
        });
    };

    useEffect(() => {
        const back = () => {
            if (!historyOwned.current) return;
            historyOwned.current = false;
            close(true);
        };
        window.addEventListener('popstate', back);
        return () => window.removeEventListener('popstate', back);
    }, [close]);

    useEffect(() => {
        if (!active) return;
        const overflow = document.body.style.overflow;
        const inertElements = Array.from(document.body.children).filter(element =>
            !element.matches('.gallery-image-overview, .gallery-carousel-photoswipe, script, style'));
        const inertState = inertElements.map(element => [element, element.inert]);
        inertElements.forEach(element => { element.inert = true; });
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = overflow;
            inertState.forEach(([element, inert]) => { element.inert = inert; });
        };
    }, [active]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            if (viewer.current) {
                viewer.current.options.hideAnimationDuration = 0;
                viewer.current.close();
            }
            releaseHistory();
            const previous = origin.current;
            if (previous) {
                window.history.scrollRestoration = previous.scrollRestoration;
                previous.element?.focus?.({preventScroll: true});
            }
        };
    }, [releaseHistory]);

    const closeToPage = useCallback(() => close(), [close]);
    return {session, showOverview, showImage, close: closeToPage, afterOverviewLeave, onViewerOpen, overviewRef,
        overviewScroll, style: origin.current?.style};
}
